import {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react"
import { configureApi, publicGet } from "../api/client"
import type { AppConfig } from "../api/types"
import { createDevAuth } from "./dev"
import type { AuthAdapter, AuthUser } from "./types"

export type { AuthAdapter, AuthUser } from "./types"

export type AuthStatus = "loading" | "signed-out" | "signed-in"

export interface AuthState {
	status: AuthStatus
	user: AuthUser | null
	/** Present when no sign-in method could be set up. */
	error: string | null
	config: AppConfig | null
	adapter: AuthAdapter | null
	getToken(): Promise<string>
	signOut(): Promise<void>
	/** Called by an adapter's SignIn component. */
	completeSignIn(u: AuthUser): void
}

const AuthContext = createContext<AuthState | null>(null)

export function useAuth(): AuthState {
	const v = useContext(AuthContext)
	if (!v) throw new Error("useAuth outside <AuthProvider>")
	return v
}

async function pick(cfg: AppConfig & { neonAuthProviders?: string[] }): Promise<AuthAdapter> {
	// A non-null neonAuthUrl means Neon Auth is configured; the browser still talks to
	// our own /api/auth proxy so the session cookie is first-party (Safari / ITP).
	// Loaded on demand: the SDK is only needed when Neon Auth is on.
	if (cfg.neonAuthUrl) {
		const { createNeonAuth, neonProxyUrl } = await import("./neon")
		return createNeonAuth(neonProxyUrl(), { providers: cfg.neonAuthProviders })
	}
	if (cfg.devAuth) return createDevAuth()
	throw new Error("No sign-in method is configured on this server")
}

export function AuthProvider(props: {
	children: ReactNode
	adapter?: AuthAdapter
	config?: AppConfig
}) {
	const [adapter, setAdapter] = useState<AuthAdapter | null>(props.adapter ?? null)
	const [config, setConfig] = useState<AppConfig | null>(props.config ?? null)
	const [status, setStatus] = useState<AuthStatus>("loading")
	const [user, setUser] = useState<AuthUser | null>(null)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		let gone = false
		;(async () => {
			try {
				let a = props.adapter ?? null
				if (!a) {
					const cfg = await publicGet<AppConfig>("/api/config")
					if (gone) return
					setConfig(cfg)
					a = await pick(cfg)
					if (gone) return
				}
				setAdapter(a)
				const u = await a.restore()
				if (gone) return
				setUser(u)
				setStatus(u ? "signed-in" : "signed-out")
			} catch (e) {
				if (gone) return
				setError((e as Error).message)
				setStatus("signed-out")
			}
		})()
		return () => {
			gone = true
		}
	}, [props.adapter])

	const signOut = useCallback(async () => {
		await adapter?.signOut()
		setUser(null)
		setStatus("signed-out")
	}, [adapter])

	const getToken = useCallback(async () => {
		if (!adapter) throw new Error("Not signed in")
		return adapter.getToken()
	}, [adapter])

	// Assigned during render (not in an effect) so children's mount effects
	// already see the right token source.
	configureApi({ getToken, onUnauthorized: () => void signOut() })

	const completeSignIn = useCallback((u: AuthUser) => {
		setUser(u)
		setStatus("signed-in")
	}, [])

	const value = useMemo<AuthState>(
		() => ({ status, user, error, config, adapter, getToken, signOut, completeSignIn }),
		[status, user, error, config, adapter, getToken, signOut, completeSignIn],
	)
	return createElement(AuthContext.Provider, { value }, props.children)
}
